import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthProvider';
import { useScreen } from '../lib/useScreen';
import { useTheme } from '../lib/useTheme';
import { supabase } from '../lib/supabaseClient';

const font = "'DM Sans', sans-serif";
const serif = "'DM Serif Display', serif";

const TYPE_COLORS = {
  identidad: '#534AB7',
  vision: '#FDBE02',
  miedo: '#eb636b',
  accion: '#1D9E75',
  skill: '#5dc1b9',
};

const TYPE_LABELS = {
  identidad: { emoji: '🪞', name: 'Identidad', desc: 'Quién eres, qué te define' },
  vision: { emoji: '🔭', name: 'Visión', desc: 'A dónde quieres llegar' },
  miedo: { emoji: '🌊', name: 'Miedo', desc: 'Lo que te frena, sin filtro' },
  accion: { emoji: '⚡', name: 'Acción', desc: 'Lo que vas a hacer, concreto' },
  skill: { emoji: '💎', name: 'Habilidad', desc: 'Lo que sabes hacer bien' },
};

const TYPES = Object.keys(TYPE_COLORS);

export default function CrearPensamiento() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { isMobile } = useScreen();
  const t = useTheme();

  const preType = searchParams.get('type') || '';
  const fromDay = searchParams.get('from_day') || null;

  const [selectedType, setSelectedType] = useState(TYPES.includes(preType) ? preType : '');
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const color = TYPE_COLORS[selectedType] || '#5dc1b9';
  const typeInfo = TYPE_LABELS[selectedType] || null;

  const handleSave = async () => {
    if (!user || !content.trim() || !selectedType || saving) return;
    setSaving(true);

    const { error } = await supabase.from('thoughts').insert({
      user_id: user.id,
      content: content.trim(),
      thought_type: selectedType,
      from_radar_day: fromDay ? parseInt(fromDay) : null,
      is_public: true,
    });

    if (error) {
      console.error('[CrearPensamiento] Error:', error.message);
      alert('Error al guardar: ' + error.message);
    } else {
      setSaved(true);
      setTimeout(() => navigate(-1), 1500);
    }
    setSaving(false);
  };

  if (saved) {
    return (
      <div style={{ minHeight: '100vh', background: t.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: font, color: '#fff' }}>
        <div style={{ textAlign: 'center', animation: 'fadeIn 0.4s ease' }}>
          <p style={{ fontSize: 48, margin: '0 0 12px' }}>✨</p>
          <p style={{ fontFamily: serif, fontSize: 22, margin: '0 0 8px' }}>Pensamiento publicado</p>
          <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)' }}>Tu neurona en la red acaba de brillar más.</p>
        </div>
        <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: t.bg, fontFamily: font, color: '#fff' }}>
      <div style={{ maxWidth: 560, margin: '0 auto', padding: isMobile ? '1.5rem 1rem' : '2.5rem 1.5rem' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <button onClick={() => navigate(-1)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 14, cursor: 'pointer', fontFamily: font }}>
            ← Volver
          </button>
          {fromDay && (
            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', background: 'rgba(255,255,255,0.05)', padding: '4px 10px', borderRadius: 50 }}>
              Día {fromDay} de Tu Radar
            </span>
          )}
        </div>

        <h1 style={{ fontFamily: serif, fontSize: isMobile ? 22 : 28, fontWeight: 400, margin: '0 0 8px' }}>
          Nuevo pensamiento
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', margin: '0 0 24px' }}>
          Escribe lo que piensas. Esto aparece en tu neurona de la red.
        </p>

        {/* Type selector */}
        <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', margin: '0 0 10px', fontWeight: 500 }}>Tipo de pensamiento</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
          {TYPES.map(type => {
            const info = TYPE_LABELS[type];
            const isSelected = selectedType === type;
            const c = TYPE_COLORS[type];
            return (
              <button key={type} onClick={() => setSelectedType(type)} style={{
                padding: '8px 16px', borderRadius: 50, fontSize: 13, fontFamily: font, cursor: 'pointer',
                border: isSelected ? `1px solid ${c}` : '0.5px solid rgba(255,255,255,0.1)',
                background: isSelected ? `${c}15` : 'transparent',
                color: isSelected ? c : 'rgba(255,255,255,0.6)',
                transition: 'all 0.2s',
              }}>
                {info.emoji} {info.name}
              </button>
            );
          })}
        </div>

        {/* Type description */}
        {typeInfo && (
          <div style={{ padding: '12px 16px', borderRadius: 12, borderLeft: `3px solid ${color}`, background: `${color}08`, marginBottom: 20 }}>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', margin: 0 }}>
              {typeInfo.emoji} <strong style={{ color }}>{typeInfo.name}:</strong> {typeInfo.desc}
            </p>
          </div>
        )}

        {/* Content editor */}
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          placeholder="Escribe tu pensamiento aquí..."
          rows={8}
          style={{
            width: '100%', padding: '16px', fontSize: 15, lineHeight: 1.7,
            background: 'rgba(255,255,255,0.04)', border: `1px solid ${selectedType ? color + '40' : 'rgba(255,255,255,0.1)'}`,
            borderRadius: 16, color: '#fff', outline: 'none', fontFamily: font,
            resize: 'vertical', minHeight: 180, boxSizing: 'border-box',
            transition: 'border-color 0.3s',
          }}
        />

        <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', margin: '8px 0 20px' }}>
          Esto es público. Los demás emprendedores podrán verlo en la red.
        </p>

        {/* Save button */}
        <button
          onClick={handleSave}
          disabled={!content.trim() || !selectedType || saving}
          style={{
            width: '100%', padding: 16, fontSize: 16, fontWeight: 500,
            background: content.trim() && selectedType ? `linear-gradient(135deg, ${color}, ${color}cc)` : 'rgba(255,255,255,0.06)',
            color: content.trim() && selectedType ? '#fff' : 'rgba(255,255,255,0.3)',
            border: 'none', borderRadius: 50, cursor: content.trim() && selectedType ? 'pointer' : 'not-allowed',
            fontFamily: font, transition: 'all 0.3s',
            boxShadow: content.trim() && selectedType ? `0 0 30px ${color}30` : 'none',
          }}
        >
          {saving ? 'Publicando...' : 'Publicar pensamiento →'}
        </button>
      </div>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}
